import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import pushNotificationService from './services/pushNotifications';

const NotificationsHistoryView = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = useCallback(() => {
    setNotifications(pushNotificationService.getNotificationHistory());
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadNotifications();

    const handler = () => loadNotifications();
    window.addEventListener('notificationHistoryUpdated', handler);
    return () => window.removeEventListener('notificationHistoryUpdated', handler);
  }, [loadNotifications]);

  const handleNotificationClick = (notification) => {
    pushNotificationService.markAsRead(notification.id);

    const { articleId, section, slug, url } = notification.data || {};
    if (url) {
      window.location.href = url;
    } else if (articleId && section) {
      const sectionSlug = section
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      const articleSlug = slug || articleId;
      navigate(`/${sectionSlug}/${articleId}/${articleSlug}`);
    }
  };

  const handleMarkAllRead = () => {
    pushNotificationService.markAllAsRead();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="app-container">
      <div className="content-container">

        {/* Fondo safe area iOS */}
        <div 
          className="absolute inset-x-0 top-0"
          style={{
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
            height: 'calc(70px + env(safe-area-inset-top))',
            marginTop: 'calc(-1 * env(safe-area-inset-top))',
            zIndex: 1
          }}
        />
        
        {/* Header */}
        <div 
          className="relative text-white px-5"
          style={{
            paddingTop: 'max(env(safe-area-inset-top), 15px)',
            paddingBottom: '15px',
            minHeight: 'calc(70px + env(safe-area-inset-top))',
            zIndex: 10,
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)'
          }}
        >
          <div className="flex items-center justify-between" style={{ paddingTop: '15px' }}>
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
            >
              ←
            </button>
            
            <h1 className="text-lg font-semibold text-center flex-1 mx-4">
              Notificaciones
            </h1>
            
            <div className="w-9 h-9" />
          </div>
        </div>

        {/* Contenido */}
        <div className="bg-white" style={{ minHeight: '100vh' }}>
          
          {/* Barra de acciones */}
          {notifications.length > 0 && unreadCount > 0 && (
            <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100">
              <span className="text-sm text-gray-500">
                {unreadCount} sin leer
              </span>
              <button
                onClick={handleMarkAllRead}
                className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                Marcar todas como leídas
              </button>
            </div>
          )}

          {/* Lista de notificaciones */}
          {notifications.length > 0 ? (
            <div>
              {notifications.map((notification) => {
                const hasLink = notification.data?.articleId || notification.data?.url;
                return (
                  <div
                    key={notification.id}
                    onClick={() => hasLink && handleNotificationClick(notification)}
                    className={`px-5 py-4 border-b border-gray-100 transition-colors ${
                      hasLink ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100' : ''
                    } ${!notification.read ? 'bg-red-50 bg-opacity-40' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Indicador no leída */}
                      <div className="flex-shrink-0 pt-1.5">
                        {!notification.read ? (
                          <div 
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: '#c82318' }}
                          />
                        ) : (
                          <div className="w-2.5 h-2.5" />
                        )}
                      </div>

                      {/* Imagen si existe */}
                      {notification.image && (
                        <div className="flex-shrink-0">
                          <img
                            src={notification.image}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`text-sm leading-snug ${
                            !notification.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                          }`}>
                            {notification.title}
                          </h3>
                          <span className="text-xs text-gray-400 flex-shrink-0 pt-0.5">
                            {formatDate(notification.receivedAt)}
                          </span>
                        </div>
                        {notification.body && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                            {notification.body}
                          </p>
                        )}
                        {hasLink && (
                          <span className="text-xs text-red-600 mt-1.5 inline-block font-medium">
                            Ver artículo ›
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Estado vacío */
            <div className="flex flex-col items-center justify-center py-20 px-8">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: '#fef2f2' }}
              >
                <span className="text-4xl">🔔</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Sin notificaciones
              </h3>
              <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs">
                Aquí aparecerán las notificaciones que recibas. Te avisaremos cuando haya nuevo contenido.
              </p>
            </div>
          )}
        </div>

        <div style={{ height: '40px' }} />
      </div>
    </div>
  );
};

export default NotificationsHistoryView;
